import { useCallback, useEffect, useMemo, useState } from 'react';
import ModulLayout from '../layout/ModulLayout';
import ListToolbar, { PohledPill } from '../layout/ListToolbar';
import KDoreseniRadek, { KDoreseniSeznam } from '../layout/KDoreseni';
import ZakaznikKarta from './ZakaznikKarta';
import { FofrKartaSouhrn } from './FofrKartaNastaveni';
import { SECTION_COLOR, SECTION_BG, fmtKc, Nadpis, RadekPanelu } from '../objednavky/ui';
import { FAZE, UROVNE, UrovenOdznak } from './ui';

/**
 * Zákazníci → Věrnost (`zakaznici.vernost`, od 2026-10-12).
 *
 * Úrovně FofrTýmu podle útraty za 12 měsíců (Rekrut, Bojovník od 3 000 Kč,
 * Strážce sklepení od 8 000 Kč — `App\Support\SegmentyZakazniku`). Body
 * jsou zatím z Objedname (1 bod = 1 Kč); vlastní Fofry v OS přijdou, až
 * se rozhodne varianta z návrhu FofrTým. Detail = karta hosta.
 * Panel: body v oběhu (závazek), uplatněno, vývoj úrovní ze snímků.
 */

const POHLEDY = [
    { id: 'strazce', label: 'Strážci' },
    { id: 'bojovnik', label: 'Bojovníci' },
    { id: 'rekrut', label: 'Rekruti' },
    { id: 'blizko', label: 'Blízko další úrovně' },
    { id: 's_body', label: 'S body' },
];

export default function VernostModul({ isDark, isMobile, cardStyle, inputStyle, mutedColor, rezervaDole = null, onObjednavka, onRezervace, onKomunikace }) {
    const [data, setData] = useState(null);
    const [prehled, setPrehled] = useState(null);
    const [chyba, setChyba] = useState(null);
    const [pohled, setPohled] = useState('strazce');
    const [hledani, setHledani] = useState('');
    const [hledaniServer, setHledaniServer] = useState('');
    const [vybranyId, setVybranyId] = useState(null);

    useEffect(() => { const t = setTimeout(() => setHledaniServer(hledani.trim()), 350); return () => clearTimeout(t); }, [hledani]);

    const nacti = useCallback(async () => {
        const p = new URLSearchParams({ pohled });
        if (hledaniServer) p.set('q', hledaniServer);
        try {
            const r = await fetch(`/api/zakaznici/vernost?${p}`, { headers: { Accept: 'application/json' } });
            if (!r.ok) throw new Error(r.status === 403 ? 'Nemáte přístup k věrnosti.' : 'Členy se nepodařilo načíst.');
            setData(await r.json()); setChyba(null);
        } catch (e) { setChyba(e.message); setData(d => d || { clenove: [], pocty: {} }); }
    }, [pohled, hledaniServer]);
    useEffect(() => { setData(d => (d ? { ...d, clenove: null } : d)); nacti(); }, [nacti]);
    useEffect(() => {
        fetch('/api/zakaznici/vernost/prehled', { headers: { Accept: 'application/json' } })
            .then(r => (r.ok ? r.json() : null)).then(d => d && setPrehled(d)).catch(() => {});
    }, []);

    const cara = isDark ? 'rgba(255,255,255,0.06)' : 'var(--border-subtle)';
    const muted = mutedColor;
    const pocty = data?.pocty || {};

    const kDoreseni = useMemo(() => {
        const p = [];
        if (pocty.blizko > 0) p.push({ id: 'blizko', pohled: 'blizko', problem: false, titulek: `${pocty.blizko} lidí má do další úrovně méně než 1 000 Kč`, popis: 'vhodní pro kampaň „ještě kousek"' });
        if (prehled && prehled.drzitelu_bodu > 0 && prehled.uplatneno_30d === 0) p.push({ id: 'body', problem: true, titulek: 'Za 30 dní se neuplatnil žádný bod', popis: 'ověřit synchronizaci bodů z Objedname' });
        return p;
    }, [pocty, prehled]);

    const souhrn = () => {
        if (!prehled) return <p className="text-xs" style={{ color: muted }}>Načítám…</p>;
        const posledni = prehled.vyvoj?.[prehled.vyvoj.length - 1] || {};
        const prvni = prehled.vyvoj?.[0] || {};
        const max = Math.max(1, ...(prehled.vyvoj || []).map(v => (v.strazce || 0) + (v.bojovnik || 0)));
        return (
            <>
                <Nadpis muted={muted}>Úrovně FofrTýmu</Nadpis>
                {Object.entries(UROVNE).reverse().map(([k, u]) => (
                    <RadekPanelu key={k} popis={u.label} hodnota={(pocty[k] ?? 0).toLocaleString('cs-CZ')} muted={muted} cara={cara} />
                ))}
                {prehled.vyvoj?.length > 1 && (
                    <>
                        <Nadpis muted={muted} style={{ marginTop: 16 }}>Strážci a Bojovníci za 12 týdnů</Nadpis>
                        <div className="flex items-end" style={{ gap: 3, height: 44 }} aria-label="Vývoj vyšších úrovní">
                            {prehled.vyvoj.map(v => (
                                <div key={v.datum} title={`${v.datum}: Strážci ${v.strazce || 0}, Bojovníci ${v.bojovnik || 0}`}
                                    style={{ flex: 1, height: `${Math.max(4, (((v.strazce || 0) + (v.bojovnik || 0)) / max) * 100)}%`, borderRadius: 2, background: SECTION_COLOR, opacity: 0.85 }} />
                            ))}
                        </div>
                        <p style={{ margin: '4px 0 0', fontSize: 11.5, color: muted }}>
                            Strážci {prvni.strazce ?? 0} → {posledni.strazce ?? 0} · Bojovníci {prvni.bojovnik ?? 0} → {posledni.bojovnik ?? 0}
                        </p>
                    </>
                )}
                <Nadpis muted={muted} style={{ marginTop: 16 }}>Body (Objedname, 1 bod = 1 Kč)</Nadpis>
                <RadekPanelu popis="V oběhu (závazek)" hodnota={fmtKc(prehled.body_v_obehu)} muted={muted} cara={cara} />
                <RadekPanelu popis="Držitelů bodů" hodnota={prehled.drzitelu_bodu.toLocaleString('cs-CZ')} muted={muted} cara={cara} />
                <RadekPanelu popis="Uplatněno tento měsíc" hodnota={fmtKc(prehled.uplatneno_mesic)} muted={muted} cara={cara} />
                <RadekPanelu popis="Platné slevové kódy" hodnota={prehled.kody_platne} muted={muted} cara={cara} />
            </>
        );
    };

    const otevriAkce = (p) => (p.pohled ? (
        <button type="button" onClick={() => setPohled(p.pohled)}
            style={{ fontSize: 12, fontWeight: 600, color: SECTION_COLOR, background: 'transparent', border: 'none', padding: 0, cursor: 'pointer', minHeight: 28 }}>
            Ukázat
        </button>
    ) : null);

    const panel = () => (
        <div className="rounded-xl" style={{ ...cardStyle, padding: 16, display: 'flex', flexDirection: 'column' }}>
            {souhrn()}
            <FofrKartaSouhrn isDark={isDark} muted={muted} cara={cara} inputStyle={inputStyle} />
            <Nadpis muted={muted} style={{ marginTop: 16 }}>K dořešení</Nadpis>
            <KDoreseniSeznam polozky={kDoreseni} isDark={isDark} renderAkce={otevriAkce} prazdne="Nic." />
        </div>
    );

    const radek = (c) => {
        const vybrany = c.id === vybranyId;
        return (
            <button key={c.id} type="button" onClick={() => setVybranyId(c.id)}
                style={{
                    display: 'flex', alignItems: 'center', gap: 12, width: '100%', textAlign: 'left', cursor: 'pointer',
                    padding: isMobile ? '10px 16px' : '9px 14px', minHeight: isMobile ? 62 : 54, border: 'none', borderBottom: `1px solid ${cara}`,
                    background: vybrany ? SECTION_BG : 'transparent', boxShadow: vybrany ? `inset 3px 0 0 ${SECTION_COLOR}` : 'none',
                }}>
                <div className="flex-1 min-w-0">
                    <p className="truncate flex items-center" style={{ margin: 0, gap: 8, fontSize: isMobile ? 15 : 13.5, fontWeight: 600, color: 'var(--text-primary)' }}>
                        <span className="truncate">{c.jmeno || 'Bez jména'}</span> <UrovenOdznak uroven={c.uroven} isDark={isDark} />
                    </p>
                    <p className="truncate" style={{ margin: '2px 0 0', fontSize: isMobile ? 12.5 : 11.5, color: c.faze === 'ohrozeny' ? '#F87171' : muted }}>
                        {c.pocet_12m} obj. za rok · {FAZE[c.faze]?.label}
                        {c.do_dalsi_kc != null ? ` · do další úrovně ${fmtKc(c.do_dalsi_kc)}` : ''}
                        {c.body > 0 ? ` · ${c.body} bodů` : ''}
                    </p>
                </div>
                <span style={{ fontSize: isMobile ? 14.5 : 13.5, fontWeight: 700, color: 'var(--text-primary)', fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>{fmtKc(c.utrata_12m)}</span>
            </button>
        );
    };

    const seznamPanel = ({ siroky }) => (
        <div className={isMobile ? 'flex flex-col flex-1 min-h-0' : 'rounded-xl overflow-hidden flex flex-col flex-1 min-h-0'} style={isMobile ? null : cardStyle}>
            <ListToolbar
                siroky={siroky} kompaktni={isMobile} sklo isDark={isDark} mutedColor={muted} inputStyle={inputStyle} barva={SECTION_COLOR}
                hledani={{ value: hledani, onChange: setHledani, placeholder: 'Hledat člena podle jména nebo kontaktu…', placeholderKratky: 'Hledat' }}
                pod={isMobile ? <KDoreseniRadek polozky={kDoreseni} isDark={isDark} barva={SECTION_COLOR} renderAkce={otevriAkce} navic={<div style={{ padding: '14px 16px' }}>{souhrn()}</div>} /> : null}
                pohledy={!siroky && !isMobile ? POHLEDY.map(p => ({ id: p.id, label: p.label, pocet: pocty[p.id] })) : null}
                pohledAktivni={pohled}
                onPohled={setPohled}
                pohled={!siroky && !isMobile ? null : POHLEDY.map(p => (
                    <PohledPill key={p.id} label={p.label} pocet={pocty[p.id] ?? null} aktivni={pohled === p.id} onClick={() => setPohled(p.id)}
                        isDark={isDark} mutedColor={muted} barva={SECTION_COLOR} kompaktni={isMobile} />
                ))}
            />
            <div className="flex-1" style={{ overflowY: 'auto', minHeight: 0 }}>
                {chyba && <p className="text-sm text-center" style={{ color: '#F87171', padding: '24px 16px' }}>{chyba}</p>}
                {(!data || data.clenove === null) && !chyba && <p className="text-center text-sm" style={{ color: muted, padding: 32 }}>Načítám…</p>}
                {data?.clenove && !chyba && data.clenove.length === 0 && <p className="text-center text-sm" style={{ color: muted, padding: '32px 16px' }}>Nikdo tu není.</p>}
                {(data?.clenove || []).map(radek)}
                {rezervaDole && <div aria-hidden="true" style={{ height: rezervaDole }} />}
            </div>
        </div>
    );

    const detail = () => (
        <div className={isMobile ? 'flex flex-col flex-1 min-h-0' : 'rounded-xl overflow-hidden flex flex-col flex-1 min-h-0'} style={isMobile ? null : cardStyle}>
            <ZakaznikKarta key={vybranyId} id={vybranyId} isDark={isDark} isMobile={isMobile} mutedColor={muted}
                onZpet={() => setVybranyId(null)} onZakaznik={setVybranyId} onObjednavka={onObjednavka} onRezervace={onRezervace}
                onKomunikace={onKomunikace} rezervaDole={isMobile ? rezervaDole : null} />
        </div>
    );

    return (
        <div className="flex flex-col" style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
            <ModulLayout isMobile={isMobile} vybrano={!!vybranyId} mobilRezim mobilPanel={false} panelVzdy barva={SECTION_COLOR} isDark={isDark}
                seznam={seznamPanel} detail={detail} panel={panel} />
        </div>
    );
}
